import React from 'react';
import { Search, X } from 'lucide-react';
import { AudienceLevel, Industry, INDUSTRY_LABELS, INDUSTRY_ORDER, LEVEL_LABELS, LEVEL_ORDER } from '../../types/pathway';
import { LIBRARY_TOPICS, ModuleLibrary, topicLabel } from '../../hooks/useModuleLibrary';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
interface LibraryFiltersProps {
  library: ModuleLibrary;
  /** Keeps the pathway audience aligned with the role used to browse modules. */
  onRoleChange: (role: AudienceLevel | 'all') => void;
  onClearFilters: () => void;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-1582" data-mp="23i0ngop8ml1y">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1583" data-mp="23hqxh1470dw9">
        {label}
      </span>
      {children}
    </div>;
}

/** Compact filter bar for browsing the full skill module library. */
export function LibraryFilters({
  library,
  onRoleChange,
  onClearFilters
}: LibraryFiltersProps) {
  return <div data-id="element-1584" data-mp="hv76k0btypag">
      <div className="grid gap-3 sm:grid-cols-3" data-id="element-1585" data-mp="cyqg2zh5ryu7">
        <Field label="Industry" data-id="element-1586" data-mp="cwvudiy2c2p2">
          <SelectDropdown ariaLabel="Search by industry" size="sm" fullWidth value={library.industry} onChange={value => library.setIndustry(value as Industry | 'all')} options={[{
          value: 'all',
          label: 'All industries'
        }, ...INDUSTRY_ORDER.map(industry => ({
          value: industry,
          label: INDUSTRY_LABELS[industry]
        }))]} data-id="element-1587" data-mp="d0tf290ah2b2" />
        </Field>
        <Field label="Role" data-id="element-1588" data-mp="mwmohjipofom">
          <SelectDropdown ariaLabel="Search by role" size="sm" fullWidth value={library.role} onChange={value => onRoleChange(value as AudienceLevel | 'all')} options={[{
          value: 'all',
          label: 'All roles'
        }, ...LEVEL_ORDER.map(level => ({
          value: level,
          label: LEVEL_LABELS[level]
        }))]} data-id="element-1589" data-mp="mxfyqugvpnbi" />
        </Field>
        <Field label="Topic" data-id="element-1590" data-mp="2z64nzx45bleb">
          <SelectDropdown ariaLabel="Search by topic" size="sm" fullWidth scrollMenu value={library.topic} onChange={library.setTopic} options={[{
          value: 'all',
          label: 'All topics'
        }, ...LIBRARY_TOPICS.map(topic => ({
          value: topic,
          label: topicLabel(topic)
        }))]} data-id="element-1591" data-mp="2z4tjtw6sgbmo" />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-1592" data-mp="2l5k756hsjzng">
        <div className="relative min-w-[14rem] flex-1" data-id="element-1593" data-mp="efmdpf9xowis">
          <label htmlFor="library-keywords" className="sr-only" data-id="element-1594" data-mp="eg5l421yk2ik">
            Search for topics or skills (optional)
          </label>
          <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-1595" data-mp="jccs7y63bk23" />
          <input id="library-keywords" value={library.keywords} onChange={e => library.setKeywords(e.target.value)} placeholder="Optional: search for topics or skills, separated by commas" className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1596" data-mp="1yzhoopqczren" />
        </div>
        <ResetFiltersButton label="Reset search" isActive={library.hasFilters} onReset={onClearFilters} data-id="element-1597" data-mp="2wkwyus4sxs2b" />
      </div>

      {library.hasFilters && <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-3" aria-label="Active filters" data-id="element-1598" data-mp="12874k9aiugue">
          <span className="mr-1 text-xs text-gray-500" data-id="element-1599" data-mp="ypi2udk7t45q">Filtering by:</span>
          {library.activeFilters.map(filter => <span key={filter.id} className="inline-flex items-center gap-1 rounded bg-[#30718D]/10 py-1 pl-2 pr-1 text-xs font-medium text-[#30718D]" data-id="element-1600" data-mp="13nryep9uq6rv">
              {filter.label}
              <button type="button" onClick={filter.id === 'role' ? () => onRoleChange('all') : filter.onRemove} aria-label={`Remove ${filter.label} filter`} className="rounded p-0.5 transition-colors hover:bg-[#30718D]/15 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1601" data-mp="1dlfsdczmva8z">
                <X size={12} aria-hidden="true" data-id="element-1602" data-mp="uppt3xj34rys" />
              </button>
            </span>)}
        </div>}
    </div>;
}